import { useEffect, useRef, useState } from "react";

function isTypingTarget(target) {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  const tagName = target.tagName?.toLowerCase();
  if (tagName === "input" || tagName === "textarea" || tagName === "select") return true;

  return Boolean(target.closest("input, textarea, select, [contenteditable='true']"));
}

function getFocusableElements(container) {
  if (!(container instanceof HTMLElement)) return [];

  const candidates = container.querySelectorAll(
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );

  return [...candidates].filter((element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (element.hasAttribute("disabled") || element.getAttribute("aria-hidden") === "true") return false;
    return element.offsetParent !== null || document.activeElement === element;
  });
}

export default function useSidebarDrawer({
  mobileMediaQuery,
  storageKey,
  onMenuToggle,
}) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [isMobileSidebar, setIsMobileSidebar] = useState(false);
  const sidebarPanelRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);
  const wasMobileDrawerOpenRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(mobileMediaQuery);
    const syncSidebarMode = (event) => {
      const matches = Boolean(event.matches);
      setIsMobileSidebar(matches);
      if (matches) {
        setSidebarCollapsed(true);
      } else {
        const savedPreference = window.localStorage.getItem(storageKey);
        setSidebarCollapsed(savedPreference === null ? true : savedPreference === "true");
      }
    };

    syncSidebarMode(mediaQuery);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", syncSidebarMode);
      return () => mediaQuery.removeEventListener("change", syncSidebarMode);
    }

    mediaQuery.addListener(syncSidebarMode);
    return () => mediaQuery.removeListener(syncSidebarMode);
  }, [mobileMediaQuery, storageKey]);

  useEffect(() => {
    if (typeof window === "undefined" || isMobileSidebar) return;
    window.localStorage.setItem(storageKey, String(sidebarCollapsed));
  }, [sidebarCollapsed, isMobileSidebar, storageKey]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleKeydown = (event) => {
      if (event.defaultPrevented || isTypingTarget(event.target)) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      if (event.key === "Escape") {
        if (isMobileSidebar && !sidebarCollapsed) {
          event.preventDefault();
          setSidebarCollapsed(true);
        }
        return;
      }

      if (event.key.toLowerCase() === "m") {
        event.preventDefault();
        setSidebarCollapsed((current) => !current);
        onMenuToggle?.();
      }
    };

    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [isMobileSidebar, sidebarCollapsed, onMenuToggle]);

  useEffect(() => {
    if (!isMobileSidebar) {
      wasMobileDrawerOpenRef.current = false;
      return undefined;
    }

    const isDrawerOpen = !sidebarCollapsed;
    const panel = sidebarPanelRef.current;

    if (!isDrawerOpen) {
      if (wasMobileDrawerOpenRef.current) {
        mobileMenuButtonRef.current?.focus();
      }
      wasMobileDrawerOpenRef.current = false;
      return undefined;
    }

    wasMobileDrawerOpenRef.current = true;

    const focusable = getFocusableElements(panel);
    (focusable[0] || panel)?.focus?.();

    const handleTrap = (event) => {
      if (event.key !== "Tab") return;

      const nextFocusable = getFocusableElements(panel);
      if (!nextFocusable.length) {
        event.preventDefault();
        panel?.focus?.();
        return;
      }

      const first = nextFocusable[0];
      const last = nextFocusable[nextFocusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleTrap);
    return () => window.removeEventListener("keydown", handleTrap);
  }, [isMobileSidebar, sidebarCollapsed]);

  const openSidebar = () => setSidebarCollapsed(false);
  const closeSidebar = () => setSidebarCollapsed(true);
  const toggleSidebar = () => {
    setSidebarCollapsed((current) => !current);
    onMenuToggle?.();
  };

  return {
    sidebarCollapsed,
    isMobileSidebar,
    sidebarPanelRef,
    mobileMenuButtonRef,
    openSidebar,
    closeSidebar,
    toggleSidebar,
    setSidebarCollapsed,
  };
}
